import GeckoRequest from 'services/gecko-request';
import { reduxStore } from 'services/gecko-store/redux';
import GeckoFeatures from 'services/gecko-features';
import GeckoLocation from 'services/gecko-location';
import moment from 'moment';
import StatsLogic from './Logic/StatsLogic';

const dispatch = (event, payload = {}, metadata = {}) => {
  return reduxStore.dispatch({
    type: event,
    payload,
    metadata,
  });
};

export const fetchConversationsByChannelStats = ({
  startDate,
  endDate,
  filteredAgents,
  filteredTeams,
  filteredBots,
  filteredTags,
  filteredChannels,
}) => {
  const params = {
    startDate: moment(startDate).startOf('day'),
    endDate: moment(endDate).endOf('day'),
    channelIds: filteredChannels,
    tagIds: filteredTags,
    botIds: filteredBots,
    agentIds: filteredAgents,
    teamIds: filteredTeams,
  };

  const stringifiedParams = {
    ...params,
    startDate: params.startDate.toString(),
    endDate: params.endDate.toString(),
  };
  dispatch('STARTED_FETCHING_CONVERSATIONS_BY_CHANNEL_STATS', params);
  return GeckoRequest.get('/stats/conversations/channel', stringifiedParams)
    .then((conversationsByChannel) => {
      dispatch('FETCHED_CONVERSATIONS_BY_CHANNEL_STATS', {
        conversationsByChannel,
      });
      return { conversationsByChannel };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_CONVERSATIONS_BY_CHANNEL_STATS', { err });
    });
};

export const updateTagsFilters = ({
  startDate,
  endDate,
  channels = [],
  tags = [],
  bots = [],
  agentsAndTeams = [],
}) => {
  const startOfStartDate = moment(startDate).startOf('day').toDate();
  const endOfEndDate = moment(endDate).endOf('day').toDate();

  // split the agents and teams into separate arrays
  const { agent: agentIds, team: teamIds } = agentsAndTeams.reduce(
    (res, id) => {
      const [key, value] = id.split('_');
      res[key].push(Number(value));
      return res;
    },
    { agent: [], team: [] }
  );

  const params = {
    startDate: startOfStartDate,
    endDate: endOfEndDate,
    filteredChannels: channels,
    filteredTags: tags,
    filteredBots: bots,
    filteredAgents: agentIds,
    filteredTeams: teamIds,
  };

  dispatch('TAGS_DATE_RANGE_CHANGE', { startDate, endDate });

  fetchTopTagsStats(params);
};

export const fetchTopTagsStats = ({
  startDate,
  endDate,
  filteredAgents,
  filteredTeams,
  filteredBots,
  filteredTags,
  filteredChannels,
}) => {
  const params = {
    startDate: moment(startDate).startOf('day'),
    endDate: moment(endDate).endOf('day'),
    channelIds: filteredChannels,
    tagIds: filteredTags,
    botIds: filteredBots,
    agentIds: filteredAgents,
    teamIds: filteredTeams,
    limit: 100,
  };

  const stringifiedParams = {
    ...params,
    startDate: params.startDate.toString(),
    endDate: params.endDate.toString(),
  };
  dispatch('STARTED_FETCHING_TOP_CONVERSATION_TAGS', params);
  return GeckoRequest.get('/stats/conversations/top-tags', stringifiedParams)
    .then((tagsWithCount) => {
      dispatch('FETCHED_TOP_CONVERSATION_TAGS', {
        tagsWithCount,
      });
      return { tagsWithCount };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_TOP_CONVERSATION_TAGS', { err });
    });
};

export const fetchResponseTime = ({
  startDate,
  endDate,
  filteredAgents,
  filteredTeams,
  filteredBots,
  filteredTags,
  filteredChannels,
}) => {
  const params = {
    startDate: moment(startDate).startOf('day'),
    endDate: moment(endDate).endOf('day'),
    channelIds: filteredChannels,
    tagIds: filteredTags,
    botIds: filteredBots,
    agentIds: filteredAgents,
    teamIds: filteredTeams,
  };

  const stringifiedParams = {
    ...params,
    startDate: params.startDate.toString(),
    endDate: params.endDate.toString(),
    webSeries: StatsLogic.splitPeriodIntoSeries(
      params.startDate,
      params.endDate
    ).map(String),
  };
  dispatch('STARTED_FETCHING_CONVERSATION_RESPONSE_TIME_STATS', params);
  dispatch('STARTED_FETCHING_CONVERSATION_RESPONSE_TIME_AGGREGATE', params);
  return Promise.all([
    GeckoRequest.post('/stats/conversations/response-time', stringifiedParams)
      .then((conversations) => {
        dispatch('FETCHED_CONVERSATION_RESPONSE_TIME_STATS', { conversations });
        return { conversations };
      })
      .catch((err) => {
        dispatch('FAILED_FETCHING_CONVERSATION_RESPONSE_TIME_STATS', { err });
      }),
    GeckoRequest.post(
      '/stats/conversations/response-time/aggregate',
      stringifiedParams
    )
      .then(({ median_first_response }) => {
        dispatch('FETCHED_CONVERSATION_RESPONSE_TIME_AGGREGATE', {
          median_first_response,
        });
        return { median_first_response };
      })
      .catch((err) => {
        dispatch('FAILED_FETCHING_CONVERSATION_RESPONSE_TIME_AGGREGATE', {
          err,
        });
      }),
  ]);
};

export const fetchMessagesSentStats = ({
  startDate,
  endDate,
  filteredAgents,
  filteredBots,
  filteredTags,
  filteredChannels,
}) => {
  const params = {
    startDate: moment(startDate).startOf('day'),
    endDate: moment(endDate).endOf('day'),
    channelIds: filteredChannels,
    tagIds: filteredTags,
    botIds: filteredBots,
    agentIds: filteredAgents,
  };
  const stringifiedParams = {
    ...params,
    startDate: params.startDate.toString(),
    endDate: params.endDate.toString(),
    webSeries: StatsLogic.splitPeriodIntoSeries(
      params.startDate,
      params.endDate
    ).map(String),
  };
  dispatch('STARTED_FETCHING_MESSAGES_SENT_STATS', params);

  if (!GeckoFeatures.can('message-stats')) {
    dispatch('FETCHED_MESSAGES_SENT_STATS', { messages: [] });
    return { messages: [] };
  }

  GeckoRequest.get('/stats/conversations/messages-sent', stringifiedParams)
    .then((messages) => {
      dispatch('FETCHED_MESSAGES_SENT_STATS', { messages });
      return { messages };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_MESSAGES_SENT_STATS', { err });
    });
};

export const fetchMessagesSentSourceStats = ({
  startDate,
  endDate,
  filteredAgents,
  filteredBots,
  filteredTags,
  filteredChannels,
}) => {
  const params = {
    startDate: moment(startDate).startOf('day'),
    endDate: moment(endDate).endOf('day'),
    channelIds: filteredChannels,
    tagIds: filteredTags,
    botIds: filteredBots,
    agentIds: filteredAgents,
  };
  const stringifiedParams = {
    ...params,
    startDate: params.startDate.toString(),
    endDate: params.endDate.toString(),
  };
  dispatch('STARTED_FETCHING_MESSAGES_SENT_SOURCE_STATS', params);

  GeckoRequest.get(
    '/stats/conversations/messages-sent-source',
    stringifiedParams
  )
    .then((messages) => {
      dispatch('FETCHED_MESSAGES_SENT_SOURCE_STATS', { messages });
      console.log('FETCHED', messages);
      return { messages };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_MESSAGES_SENT_SOURCE_STATS', { err });
    });
};

export const fetchClosedConversationStats = ({
  startDate,
  endDate,
  filteredAgents,
  filteredTeams,
  filteredBots,
  filteredTags,
  filteredChannels,
}) => {
  const params = {
    startDate: moment(startDate).startOf('day'),
    endDate: moment(endDate).endOf('day'),
    channelIds: filteredChannels,
    tagIds: filteredTags,
    botIds: filteredBots,
    agentIds: filteredAgents,
    teamIds: filteredTeams,
  };

  const stringifiedParams = {
    ...params,
    startDate: params.startDate.toString(),
    endDate: params.endDate.toString(),
    webSeries: StatsLogic.splitPeriodIntoSeries(
      params.startDate,
      params.endDate
    ).map(String),
  };
  dispatch('STARTED_FETCHING_CLOSED_CONVERSATIONS_STATS', params);
  return GeckoRequest.post(
    '/stats/conversations/closed-conversations',
    stringifiedParams
  )
    .then((conversations) => {
      dispatch('FETCHED_CLOSED_CONVERSATIONS_STATS', { conversations });
      return { conversations };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_CLOSED_CONVERSATIONS_STATS', { err });
    });
};

export const fetchConversationPerHourStats = ({
  startDate,
  endDate,
  filteredAgents,
  filteredTeams,
  filteredBots,
  filteredTags,
  filteredChannels,
}) => {
  const params = {
    startDate: moment(startDate).startOf('day'),
    endDate: moment(endDate).endOf('day'),
    channelIds: filteredChannels,
    tagIds: filteredTags,
    botIds: filteredBots,
    agentIds: filteredAgents,
    teamIds: filteredTeams,
  };

  const stringifiedParams = {
    ...params,
    startDate: params.startDate.toString(),
    endDate: params.endDate.toString(),
  };
  dispatch('STARTED_FETCHING_CONVERSATIONS_PER_HOUR_STATS', params);
  dispatch('STARTED_FETCHING_WORK_HOURS', params);
  GeckoRequest.get('/account')
    .then(({ work_hours }) => {
      if (!work_hours) {
        return;
      }
      dispatch('FETCHED_WORK_HOURS', { workHours: work_hours });
      GeckoRequest.post('/stats/conversations/per-hour', {
        ...stringifiedParams,
        timezone: work_hours.timezone,
      })
        .then((conversations) => {
          dispatch('FETCHED_CONVERSATIONS_PER_HOUR_STATS', { conversations });
          return { conversations };
        })
        .catch((err) => {
          dispatch('FAILED_FETCHING_CONVERSATIONS_PER_HOUR_STATS', { err });
        });

      return { workHours: work_hours };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_WORK_HOURS', { err });
    })
    .then(() => {});
};

export const saveWorkHours = (workHours) => {
  const payload = { workHours };
  dispatch('STARTED_UPDATING_WORKHOURS', payload);
  return GeckoRequest.post(`/account/update-work-hours`, payload)
    .then(({ work_hours }) => {
      dispatch('UPDATED_WORKHOURS', { workHours: work_hours });
      return { workHours: work_hours };
    })
    .catch((err) => {
      dispatch('FAILED_UPDATING_WORKHOURS', { error: err, workHours });
      throw err;
    });
};

export const fetchNewConversationStats = ({
  startDate,
  endDate,
  filteredAgents,
  filteredTeams,
  filteredBots,
  filteredTags,
  filteredChannels,
}) => {
  const params = {
    startDate: moment(startDate).startOf('day'),
    endDate: moment(endDate).endOf('day'),
    channelIds: filteredChannels,
    tagIds: filteredTags,
    botIds: filteredBots,
    agentIds: filteredAgents,
    teamIds: filteredTeams,
  };

  const stringifiedParams = {
    ...params,
    startDate: params.startDate.toString(),
    endDate: params.endDate.toString(),
    webSeries: StatsLogic.splitPeriodIntoSeries(
      params.startDate,
      params.endDate
    ).map(String),
  };
  dispatch('STARTED_FETCHING_NEW_CONVERSATIONS_STATS', params);
  return GeckoRequest.post(
    '/stats/conversations/new-conversations',
    stringifiedParams
  )
    .then((conversations) => {
      dispatch('FETCHED_NEW_CONVERSATIONS_STATS', { conversations });
      return { conversations };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_NEW_CONVERSATIONS_STATS', { err });
    });
};

export const fetchChatGPTConversationStats = ({
  startDate,
  endDate,
  filteredAgents,
  filteredTeams,
  filteredBots,
  filteredTags,
  filteredChannels,
}) => {
  const params = {
    startDate: moment(startDate).startOf('day'),
    endDate: moment(endDate).endOf('day'),
    channelIds: filteredChannels,
    tagIds: filteredTags,
    botIds: filteredBots,
    agentIds: filteredAgents,
    teamIds: filteredTeams,
  };

  const stringifiedParams = {
    ...params,
    startDate: params.startDate.toString(),
    endDate: params.endDate.toString(),
    webSeries: StatsLogic.splitPeriodIntoSeries(
      params.startDate,
      params.endDate
    ).map(String),
  };
  dispatch('STARTED_FETCHING_CHAT_GPT_CONVERSATIONS_STATS', params);

  if (!GeckoFeatures.can('ai-engine')) {
    dispatch('FETCHED_CHAT_GPT_CONVERSATIONS_STATS', { conversations: [] });
    return { conversations: [] };
  }

  return GeckoRequest.post(
    '/stats/conversations/chat-gpt-conversations',
    stringifiedParams
  )
    .then((conversations) => {
      dispatch('FETCHED_CHAT_GPT_CONVERSATIONS_STATS', { conversations });
      return { conversations };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_CHAT_GPT_CONVERSATIONS_STATS', { err });
    });
};

export const fetchConversationRatingsStats = ({
  startDate,
  endDate,
  filteredBots,
  filteredChannels,
  filteredAgents,
  filteredTeams,
}) => {
  const params = {
    startDate: moment(startDate).startOf('day'),
    endDate: moment(endDate).endOf('day'),
    channelIds: filteredChannels,
    botIds: filteredBots,
    agentIds: filteredAgents,
    teamIds: filteredTeams,
  };

  dispatch('STARTED_FETCHING_CONVERSATION_RATINGS_STATS', params);
  return GeckoRequest.post('/stats/conversations/ratings', params)
    .then((ratings) => {
      dispatch('FETCHED_CONVERSATION_RATINGS_STATS', { ratings });
      return { ratings };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_CONVERSATION_RATINGS_STATS', { err });
    });
};

export const fetchConversationLength = ({
  startDate,
  endDate,
  filteredAgents,
  filteredTeams,
  filteredBots,
  filteredTags,
  filteredChannels,
}) => {
  const params = {
    startDate: moment(startDate).startOf('day'),
    endDate: moment(endDate).endOf('day'),
    channelIds: filteredChannels,
    tagIds: filteredTags,
    botIds: filteredBots,
    agentIds: filteredAgents,
    teamIds: filteredTeams,
  };

  const stringifiedParams = {
    ...params,
    startDate: params.startDate.toString(),
    endDate: params.endDate.toString(),
    webSeries: StatsLogic.splitPeriodIntoSeries(
      params.startDate,
      params.endDate
    ).map(String),
  };

  dispatch('STARTED_FETCHING_CONVERSATION_LENGTH_STATS', params);
  dispatch('STARTED_FETCHING_CONVERSATION_LENGTH_AGGREGATE', params);
  return Promise.all([
    GeckoRequest.post(
      '/stats/conversations/conversation-length',
      stringifiedParams
    )
      .then((conversations) => {
        dispatch('FETCHED_CONVERSATIONS_LENGTH_STATS', { conversations });
        return { conversations };
      })
      .catch((err) => {
        dispatch('FAILED_FETCHING_CONVERSATIONS_LENGTH_STATS', { err });
      }),
    GeckoRequest.post(
      '/stats/conversations/conversation-length/aggregate',
      stringifiedParams
    )
      .then(({ median_conversation_length }) => {
        dispatch('FETCHED_CONVERSATION_LENGTH_AGGREGATE', {
          median_conversation_length,
        });
        return { median_conversation_length };
      })
      .catch((err) => {
        dispatch('FAILED_FETCHING_CONVERSATION_LENGTH_AGGREGATE', { err });
      }),
  ]);
};

export const fetchAgentStats = (options = {}) => {
  const startDate = options.startDate
    ? moment(options.startDate).format('YYYY-MM-DD')
    : null;
  const endDate = options.endDate
    ? moment(options.endDate).format('YYYY-MM-DD')
    : null;
  const stringifiedParams = {
    startDate,
    endDate,
    channelIds: options.filteredChannels,
    tagIds: options.filteredTags,
    teamIds: options.filteredTeams,
  };

  dispatch('START_FETCHING_AGENT_STATS', {});
  return GeckoRequest.get('/stats/agents', stringifiedParams)
    .then((agents) => {
      agents = agents.map((agent) => {
        agent.full_name = agent.first_name + ' ' + agent.last_name;
        agent.number_messages = agent.number_messages || 0;
        agent.number_conversations = agent.number_conversations || 0;
        agent.median_response_time = StatsLogic.median(
          agent.first_response_times
            ? agent.first_response_times.filter((time) => !!time)
            : null
        );
        agent.median_time_to_close = StatsLogic.median(
          agent.conversation_lengths
            ? agent.conversation_lengths.filter((length) => !!length)
            : null
        );
        return agent;
      });
      dispatch('UPDATE_AGENT_STATS', { agentStats: agents });
    })
    .catch((err) => {
      console.error('error', err);
    });
};

export const fetchTeamStats = (options = {}) => {
  const startDate = options.startDate
    ? moment(options.startDate).format('YYYY-MM-DD')
    : null;
  const endDate = options.endDate
    ? moment(options.endDate).format('YYYY-MM-DD')
    : null;
  const stringifiedParams = {
    startDate,
    endDate,
    channelIds: options.filteredChannels,
    tagIds: options.filteredTags,
    teamIds: options.filteredTeams,
  };

  dispatch('START_FETCHING_TEAM_STATS', {});

  return GeckoRequest.get('/stats/teams', stringifiedParams)
    .then((teams) => {
      teams = teams.map((team) => {
        team.number_messages = team.number_messages || 0;
        team.agents_conversations = team.agents_conversations || 0;
        team.team_conversations = team.team_conversations || 0;
        team.median_response_time = StatsLogic.median(
          team.first_response_times
            ? team.first_response_times.filter((time) => !!time)
            : null
        );
        team.median_time_to_close = StatsLogic.median(
          team.conversation_lengths
            ? team.conversation_lengths.filter((time) => !!time)
            : null
        );
        return team;
      });
      dispatch('UPDATE_TEAM_STATS', { teamStats: teams });
    })
    .catch((err) => {
      console.error('error', err);
      dispatch('FAILED_FETCHING_TEAM_STATS', { err });
    });
};

export const updateTeamFilters = ({
  startDate,
  endDate,
  filteredChannels,
  filteredTags,
  filteredTeams,
}) => {
  dispatch('TEAMS_DATE_RANGE_CHANGE', { startDate, endDate });

  const params = {
    startDate,
    endDate,
    filteredChannels,
    filteredTags,
    filteredTeams,
  };

  fetchTeamStats(params);
  fetchAgentStats(params);
};

export const fetchBotsResponses = (options = {}) => {
  const startDate = options.startDate ? String(options.startDate) : null;
  const endDate = options.endDate ? String(options.endDate) : null;
  const stringifiedParams = {
    startDate,
    endDate,
    channelIds: options.filteredChannels,
    tagIds: options.filteredTags,
    botIds: options.filteredBots,
  };
  dispatch('START_FETCHING_BOTS_RESPONSES', {});
  return GeckoRequest.get('/stats/bots/successful-responses', stringifiedParams)
    .then((botsResponses) => {
      dispatch('UPDATE_BOTS_RESPONSES', { botsResponses });
      return { botsResponses };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_BOTS_RESPONSES', { err });
    });
};

export const fetchBotsTimeSaved = (options = {}) => {
  const startDate = options.startDate ? String(options.startDate) : null;
  const endDate = options.endDate ? String(options.endDate) : null;
  const stringifiedParams = {
    startDate,
    endDate,
    channelIds: options.filteredChannels,
    tagIds: options.filteredTags,
    botIds: options.filteredBots,
  };
  dispatch('START_FETCHING_TIME_SAVED', {});
  return GeckoRequest.get('/stats/bots/time-saved', stringifiedParams)
    .then((botsTimeSaved) => {
      dispatch('UPDATE_TIME_SAVED', { botsTimeSaved });
      return { botsTimeSaved };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_TIME_SAVED', { err });
    });
};

export const fetchResolvedConversations = (options = {}) => {
  // Configure Params
  const startDate = options.startDate ? String(options.startDate) : null;
  const endDate = options.endDate ? String(options.endDate) : null;
  const stringifiedParams = {
    startDate,
    endDate,
    channelIds: options.filteredChannels,
    tagIds: options.filteredTags,
    botIds: options.filteredBots,
  };

  dispatch('START_FETCHING_BOT_RESOLVED_CONVERSATIONS', {});
  return GeckoRequest.get(
    '/stats/bots/resolved-conversations',
    stringifiedParams
  )
    .then((botsResolvedConversations) => {
      dispatch('UPDATE_BOTS_RESOLVED_CONVERSATIONS', {
        botsResolvedConversations,
      });
      return { botsResolvedConversations };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_BOTS_RESOLVED_CONVERSATIONS', { err });
    });
};

export const fetchBotsConversationLength = (options = {}) => {
  const startDate = options.startDate ? String(options.startDate) : null;
  const endDate = options.endDate ? String(options.endDate) : null;
  const stringifiedParams = {
    startDate,
    endDate,
    channelIds: options.filteredChannels,
    tagIds: options.filteredTags,
    botIds: options.filteredBots,
  };

  dispatch('START_FETCHING_BOTS_CONVERSATION_LENGTH', {});
  return GeckoRequest.get('/stats/bots/conversation-length', stringifiedParams)
    .then((botsConversationLength) => {
      dispatch('UPDATE_BOTS_CONVERSATION_LENGTH', { botsConversationLength });
      return { botsConversationLength };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_BOTS_CONVERSATION_LENGTH', { err });
    });
};
export const fetchClosedConversations = (options = {}) => {
  const startDate = options.startDate ? String(options.startDate) : null;
  const endDate = options.endDate ? String(options.endDate) : null;
  const stringifiedParams = {
    startDate,
    endDate,
    channelIds: options.filteredChannels,
    tagIds: options.filteredTags,
    botIds: options.filteredBots,
  };
  dispatch('START_FETCHING_CLOSED_CONVERSATIONS', {});
  return GeckoRequest.get('/stats/bots/closed-conversations', stringifiedParams)
    .then((botsClosedConversations) => {
      dispatch('UPDATE_CLOSED_CONVERSATIONS', { botsClosedConversations });
      return { botsClosedConversations };
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_CLOSED_CONVERSATIONS', { err });
    });
};

export const updateBotsFilters = ({
  startDate,
  endDate,
  filteredChannels = [],
  filteredTags = [],
  filteredBots = [],
}) => {
  dispatch('BOTS_DATE_RANGE_CHANGE', { startDate, endDate });
  const startOfStartDate = moment(startDate).startOf('day').toDate();
  const endOfEndDate = moment(endDate).endOf('day').toDate();

  const params = {
    startDate: startOfStartDate,
    endDate: endOfEndDate,
    filteredChannels,
    filteredTags,
    filteredBots,
  };

  fetchBotsResponses(params);
  fetchBotsTimeSaved(params);
  fetchResolvedConversations(params);
  fetchBotsConversationLength(params);
  fetchClosedConversations(params);
};

export const updateFilters = ({
  predefinedRange,
  customDateRangeEnabled,
  startDate,
  endDate,
  filteredAgents = [],
  filteredBots = [],
  filteredTags = [],
  filteredChannels = [],
}) => {
  const fullState = reduxStore.getState();
  const currentState = fullState.statsRoute;
  const agentTeams = fullState.agent_teams;

  // Direct assignments are checked with conversationsByChannel.
  let directAgents = [];
  let filteredTeams = [];
  // Add all agent IDs either as is or from teams to an Array
  const _filteredAgents = filteredAgents
    .reduce((agents, item) => {
      const isAgent = item.indexOf('agent_') === 0;
      if (isAgent) {
        const newAgent = Number(item.replace('agent_', ''));
        directAgents.push(newAgent);
        return agents.concat(newAgent);
      } else {
        const teamId = Number(item.replace('team_', ''));
        filteredTeams.push(teamId);
        const agentIds = agentTeams
          .filter((at) => at.team_id === teamId)
          .map((at) => at.agent_id);
        return agents.concat(agentIds);
      }
    }, [])
    .filter((agent_id, index, agents) => agents.indexOf(agent_id) === index); // Deduplicate the list

  const params = {
    startDate,
    endDate,
    filteredAgents: directAgents,
    filteredTeams,
    filteredBots,
    filteredTags,
    filteredChannels,
  };
  const fetchClosedConversations = fetchClosedConversationStats(params);
  const fetchNewConversations = fetchNewConversationStats(params);
  const fetchChatGPTConversations = fetchChatGPTConversationStats(params);
  const fetchConversationRatings = fetchConversationRatingsStats(params);
  const fetchConversationLengths = fetchConversationLength(params);
  const fetchConversationResponseTime = fetchResponseTime(params);
  const fetchConversationsByChannel = fetchConversationsByChannelStats(params);
  const fetchTopTags = fetchTopTagsStats(params);
  const fetchConversationPerHour = fetchConversationPerHourStats(params);
  // Fetch Messages Sent only uses Agents for sender_id rather than assignments, so use all agents.
  const fetchMessagesSent = fetchMessagesSentStats({
    ...params,
    filteredAgents: _filteredAgents,
  });
  const fetchMessagesSentSource = fetchMessagesSentSourceStats({
    ...params,
    filteredAgents: _filteredAgents,
  });

  if (predefinedRange !== currentState.conversationPredefinedDateRange) {
    dispatch('CONVERSATION_PREDEFINED_DATE_RANGE_CHANGED', { predefinedRange });
  }
  if (customDateRangeEnabled !== currentState.conversationCustomDateEnabled) {
    dispatch('CONVERSATION_CUSTOM_DATE_RANGE_TOGGLED', {
      customDateRangeEnabled,
    });
  }
  if (
    startDate.toString() !== currentState.conversationStartDateFilter ||
    endDate.toString() !== currentState.conversationEndDateFilter
  ) {
    dispatch('CONVERSATION_DATE_RANGE_CHANGED', { startDate, endDate });
  }
  if (
    JSON.stringify(_filteredAgents) !==
    JSON.stringify(currentState.filteredAgents)
  ) {
    dispatch('CONVERSATION_AGENT_FILTER_CHANGED', {
      filteredAgents: _filteredAgents, // This is just the ids
      filteredAgentsAndTeams: filteredAgents, // This is the agents and teams like agent_3, team_1
    });
  }
  if (
    JSON.stringify(filteredBots) !== JSON.stringify(currentState.filteredBots)
  ) {
    dispatch('CONVERSATION_BOTS_FILTER_CHANGED', { filteredBots });
  }
  if (
    JSON.stringify(filteredTags) !== JSON.stringify(currentState.filteredTags)
  ) {
    dispatch('CONVERSATION_TAGS_FILTER_CHANGED', { filteredTags });
  }
  if (
    JSON.stringify(filteredChannels) !==
    JSON.stringify(currentState.filteredChannels)
  ) {
    dispatch('CONVERSATION_CHANNELS_FILTER_CHANGED', { filteredChannels });
  }
  dispatch('STARTED_FETCHING_CONVERSATION_STATS');
  Promise.all([
    fetchConversationsByChannel,
    fetchNewConversations,
    fetchChatGPTConversations,
    fetchConversationRatings,
    fetchClosedConversations,
    fetchConversationLengths,
    fetchConversationResponseTime,
    fetchMessagesSent,
    fetchMessagesSentSource,
    fetchConversationPerHour,
    fetchTopTags,
  ])
    .then(() => {
      dispatch('CONVERSATION_STATS_FILTERS_UPDATED');
      dispatch('FETCHED_CONVERSATION_STATS');
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_CONVERSATION_STATS');
    });
};

export const goToRatings = (rating) => {
  dispatch('FILTERED_RATINGS_CHANGED', {
    filteredRatings: rating ? [rating] : [],
  });
  dispatch('RATINGS_SHOULD_USE_CONVERSATION_FILTERS', { useFilters: true });
  GeckoLocation.goTo('/stats/ratings', {});
};
// Anchor need to fix this
export const goToTags = () => {
  dispatch('TAGS_SHOULD_USE_CONVERSATION_FILTERS', { useFilters: true });
  GeckoLocation.goTo('/stats/tags', {});
};

export const resetRatingsUsingConversationFilters = () => {
  dispatch('RATINGS_SHOULD_USE_CONVERSATION_FILTERS', { useFilters: false });
};

export const resetTagsUsingConversationFilters = () => {
  dispatch('TAGS_SHOULD_USE_CONVERSATION_FILTERS', { useFilters: false });
};
